import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Grid,
  Link,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ShareIcon from '@mui/icons-material/Share';
import DescriptionIcon from '@mui/icons-material/Description';
import PsychologyIcon from '@mui/icons-material/Psychology';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InsightsIcon from '@mui/icons-material/Insights';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import DownloadIcon from '@mui/icons-material/Download';

/* ---------- Helpers (same behaviors as before) ---------- */
const decodeEntities = (raw) => {
  if (raw == null) return '';
  const str = String(raw);
  const quick = str
    .replace(/&#8217;|&rsquo;/g, '’')
    .replace(/&#8220;|&ldquo;/g, '“')
    .replace(/&#8221;|&rdquo;/g, '”')
    .replace(/&#8211;|&ndash;/g, '–')
    .replace(/&#8212;|&mdash;/g, '—')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const ta = document.createElement('textarea');
    ta.innerHTML = quick;
    return ta.value;
  }
  return quick;
};
const cleanText = (t) => decodeEntities((t ?? '').toString().trim());
const safeText = (t) => cleanText(t);

const formatConfidence = (c) => {
  if (c == null || c === '') return '';
  if (typeof c === 'string') return c.includes('%') ? c : c;
  if (typeof c === 'number') return `${Number(c).toFixed(1)}%`;
  return safeText(c);
};

const truncateWords = (text = '', maxWords = 70) => {
  const words = cleanText(text).split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return words.join(' ');
  return words.slice(0, maxWords).join(' ') + ' …';
};

const toObj = (v) => {
  if (!v) return null;
  if (typeof v === 'object') return v;
  if (typeof v === 'string') { try { return JSON.parse(v); } catch { return null; } }
  return null;
};

const tryGet = (fn, fallback = undefined) => {
  try { const v = fn(); return v === undefined ? fallback : v; } catch { return fallback; }
};

const extractGeminiText = (anyResp) => {
  const texts = [];
  const scan = (objIn) => {
    const obj = toObj(objIn);
    if (!obj) return;
    const roots = [obj];
    if (obj.ai_response) roots.push(toObj(obj.ai_response));

    for (const root of roots) {
      if (!root) continue;

      const cands = root && root.candidates;
      if (Array.isArray(cands)) {
        for (const c of cands) {
          const parts = c && c.content && c.content.parts;
          if (Array.isArray(parts)) {
            for (const part of parts) {
              const t = part && part.text;
              if (typeof t === 'string' && t.trim()) texts.push(decodeEntities(t.trim()));
            }
          }
        }
      }

      const parts = root && root.content && root.content.parts;
      if (Array.isArray(parts)) {
        for (const part of parts) {
          const t = part && part.text;
          if (typeof t === 'string' && t.trim()) texts.push(decodeEntities(t.trim()));
        }
      }

      if (root && typeof root.text === 'string' && root.text.trim()) {
        texts.push(decodeEntities(root.text.trim()));
      }
    }
  };
  scan(anyResp);
  return texts.join('\n\n').trim();
};

const formatDate = (d) => {
  try {
    const dt = new Date(String(d));
    if (isNaN(dt.getTime())) return '';
    return dt.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return '';
  }
};

const sourceFromUrl = (url) => {
  try {
    if (!url) return '';
    const u = new URL(url);
    return u.hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

const verdictBg = (v, darkMode) => {
  const s = (v || '').toString().trim().toLowerCase();
  if (darkMode) {
    if (s === 'true') return '#1b5e20';
    if (s === 'false') return '#b71c1c';
    if (s === 'misleading') return '#5d4037';
    return '#0d47a1';
  } else {
    if (s === 'true') return '#e8f5e9';
    if (s === 'false') return '#ffebee';
    if (s === 'misleading') return '#fff8e1';
    return '#e3f2fd';
  }
};

const verdictFg = (v, darkMode) => {
  if (darkMode) return '#ffffff';
  const s = (v || '').toString().trim().toLowerCase();
  if (s === 'true') return '#1b5e20';
  if (s === 'false') return '#b71c1c';
  if (s === 'misleading') return '#8d6e63';
  return '#0d47a1';
};

/* ---------- Component ---------- */
export default function FactCheckDisplay({
  parsedResult = null,
  darkMode,
  topRef,
  showScrollToTop,
  handleScrollToTop,
  claim,
  handleThumbsUp,
  handleThumbsDown,
  thumbsUpCount = 0,
  thumbsDownCount = 0,
  showResults,
  onRegenerate,
  onAdvancedAnalysis,
  onShare,
  advancedAnalysis,
  analysisLoading,
  showSummary = false,
  aiOnly = false,
  onlyApiResponse = false,
}) {
  // Hooks must be at the top (before any return)
  const [citOpen, setCitOpen] = React.useState(false);

  // If we’re not showing, bail early (after hooks are declared).
  if (!showResults) return null;

  const p = parsedResult || null;

  // Decide view
  const isAiView = !!(aiOnly || onlyApiResponse);

  // ---------- AI text ----------
  let geminiText = (p?.aiText && safeText(p.aiText)) || '';
  if (!geminiText) {
    const apiResp =
      tryGet(() => p.apiResp, null) ||
      tryGet(() => p.api_response_parsed, null) ||
      tryGet(() => p?.raw?.user_ai_result?.api_response_parsed, null) ||
      tryGet(() => p?.raw?.user_ai_result?.api_response, null) ||
      tryGet(() => p?.raw?.api_response_parsed, null) ||
      tryGet(() => p?.raw?.api_response, null) ||
      tryGet(() => p?.api_response, null) ||
      tryGet(() => p?.raw?.fact_check?.ai_response, null) ||
      tryGet(() => p?.fact_check?.ai_response, null) ||
      null;
    geminiText = extractGeminiText(apiResp);
  }

  const claimText =
    safeText(
      tryGet(() => p.claim && (p.claim.claim_text || p.claim.text), '') ||
      tryGet(() => p.claim_text, '') ||
      tryGet(() => p.fullText, '') ||
      tryGet(() => p.raw?.claim?.claim_text, '') ||
      claim || ''
    );

  const verdictRaw =
    tryGet(() => p.verdict) ||
    tryGet(() => p.apiResp?.verdict) ||
    tryGet(() => p.raw?.fact_check?.result) ||
    '';
  const verdict = safeText(verdictRaw);

  const confidence =
    !(isAiView)
      ? formatConfidence(
          tryGet(() => p.confidence) ||
          tryGet(() => p.apiResp?.confidence) ||
          tryGet(() => p.raw?.fact_check?.confidence) ||
          ''
        )
      : '';

  const apiRespForSummary =
    tryGet(() => p.apiResp, null) ||
    tryGet(() => p.api_response_parsed, null) ||
    tryGet(() => p.raw?.api_response_parsed, null) ||
    tryGet(() => p.raw?.api_response, null) ||
    null;

  let summary = '';
  if (isAiView) {
    summary =
      safeText(toObj(apiRespForSummary)?.summary) ||
      safeText(toObj(apiRespForSummary)?.claimSummary) ||
      safeText(toObj(apiRespForSummary)?.justification) ||
      geminiText ||
      safeText(tryGet(() => toObj(apiRespForSummary)?.wikipedia?.extract, '')) ||
      safeText(toObj(apiRespForSummary)?.text) ||
      safeText(toObj(apiRespForSummary)?.reply) ||
      truncateWords(claimText, 20);
  } else {
    summary =
      safeText(tryGet(() => p.summary, '')) ||
      safeText(tryGet(() => p.api_response?.summary, '')) ||
      safeText(tryGet(() => p.apiResp?.summary, '')) ||
      safeText(tryGet(() => p.raw?.claim_summary, '')) ||
      truncateWords(claimText, 30);
  }

  const wiki =
    tryGet(() => p.wiki, null) ||
    tryGet(() => p.apiResp?.wikipedia, null) ||
    tryGet(() => p.api_response?.wikipedia, null) ||
    tryGet(() => p.raw?.wikipedia, null) ||
    null;

  const news =
    tryGet(() => p.news, null) ||
    tryGet(() => p.apiResp?.newsArticles, null) ||
    tryGet(() => p.api_response?.newsArticles, null) ||
    tryGet(() => p.raw?.fact_check?.newsArticles, null) ||
    tryGet(() => p.raw?.news_articles, []) ||
    [];

  const googleFactCheck =
    tryGet(() => p.googleFactCheck, null) ||
    tryGet(() => p.apiResp?.googleFactCheck, null) ||
    tryGet(() => p.api_response?.googleFactCheck, null) ||
    tryGet(() => p.raw?.fact_check?.googleFactCheck, null) ||
    tryGet(() => p.raw?.google_fact_check, null) ||
    null;

  // ===== Citations helpers =====
  const getCitationText = () => {
    const articles = Array.isArray(news) ? news : [];
    if (!articles.length) return 'No citations available.';
    return articles
      .map((a) => {
        const title = a?.title || a?.headline || '';
        const source =
          a?.source || a?.source_name || a?.site || sourceFromUrl(a?.url || a?.link || '') || '';
        const url = a?.url || a?.link || '';
        const dateStr = a?.published_at || a?.publishedAt || new Date().toISOString();
        const date = new Date(dateStr).toLocaleDateString();
        return `${title}. ${source}. Retrieved ${date}, from ${url}`;
      })
      .join('\n\n');
  };

  const handleCopyCitations = async () => {
    try { await navigator.clipboard.writeText(getCitationText()); } catch {}
  };

  const handleDownloadFullResponse = () => {
    const filename = (claimText.trim() || 'fact_check').replace(/[^a-zA-Z0-9]/g, '_');
    const sections = [];
    sections.push(`=== Claim ===\n${claimText || '(no claim)'}\n`);
    if (geminiText) sections.push(`=== AI Response ===\n${geminiText}\n`);
    if (verdict) sections.push(`Verdict: ${String(verdict).toUpperCase()}`);
    if (confidence) sections.push(`Confidence: ${confidence}`);
    const just =
      p?.raw?.fact_check?.justification ??
      p?.apiResp?.justification ??
      '';
    if (just) {
      sections.push(
        `Justification:\n${typeof just === 'object' ? JSON.stringify(just, null, 2) : just}`
      );
    }
    if (wiki) {
      sections.push(`Wikipedia:\nTitle: ${wiki.title || ''}\nSnippet: ${wiki.extract || ''}`);
    }
    if (Array.isArray(news) && news.length) {
      const txt = news
        .map((a, i) => {
          const when = a.published_at || a.publishedAt || '';
          const whenFmt = when ? new Date(when).toLocaleDateString() : '';
          return `Article ${i + 1}:\nTitle: ${a.title || ''}\nDescription: ${
            a.description || a.excerpt || ''
          }\nSource: ${a.source || ''}\nPublished: ${whenFmt}\nURL: ${a.url || a.link || ''}`;
        })
        .join('\n\n');
      sections.push('News Articles:\n' + txt);
    }
    const fullText = sections.join('\n\n');
    const blob = new Blob([fullText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      {/* Verdict (claims view) */}
      {!isAiView && (verdict || claimText) && (
        <Paper
          elevation={2}
          sx={{
            bgcolor: verdictBg(verdict, !!darkMode),
            color: verdictFg(verdict, !!darkMode),
            p: 2,
            mb: 2,
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'inherit' }}>
                Verdict: {verdict ? verdict.toUpperCase() : 'N/A'}
              </Typography>
              {confidence && (
                <Typography variant="body2" sx={{ color: 'inherit' }}>
                  Confidence: {confidence}
                </Typography>
              )}
            </Grid>
            <Grid item>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  aria-label="thumbs-up"
                  onClick={handleThumbsUp}
                  size="small"
                  sx={{ color: 'inherit' }}
                >
                  <ThumbUpIcon fontSize="small" />
                </IconButton>
                <Typography component="span" sx={{ mr: 1 }}>
                  {thumbsUpCount}
                </Typography>
                <IconButton
                  aria-label="thumbs-down"
                  onClick={handleThumbsDown}
                  size="small"
                  sx={{ color: 'inherit' }}
                >
                  <ThumbDownIcon fontSize="small" />
                </IconButton>
                <Typography component="span" sx={{ mr: 1 }}>
                  {thumbsDownCount}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Justification (claims view) */}
      {!isAiView && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1">Justification</Typography>
          {safeText(p?.raw?.fact_check?.justification ?? p?.apiResp?.justification ?? '') ? (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {safeText(p?.raw?.fact_check?.justification ?? p?.apiResp?.justification)}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No explicit justification provided.
            </Typography>
          )}
        </Paper>
      )}

      {/* AI view + toolbar */}
      {isAiView && (
        <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              AI Response
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Tooltip title="Citations">
                <IconButton size="small" onClick={() => setCitOpen(true)}>
                  <LibraryBooksIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download full response">
                <IconButton size="small" onClick={handleDownloadFullResponse}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton
                size="small"
                aria-label="Play AI response"
                sx={{
                  color: (t) =>
                    t.palette.mode === 'dark' ? t.palette.primary.light : t.palette.primary.main,
                }}
                onClick={() => {
                  try {
                    const txt = (geminiText || '').toString().trim();
                    if (typeof window !== 'undefined' && 'speechSynthesis' in window && txt) {
                      window.speechSynthesis.cancel();
                      window.speechSynthesis.speak(new SpeechSynthesisUtterance(txt));
                    }
                  } catch {}
                }}
              >
                <PsychologyIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => console.log('translate')}>
                <DescriptionIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => console.log('bookmark')}>
                <BookmarkBorderIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => console.log('like')}>
                <FavoriteBorderIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={onShare}>
                <ShareIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Typography
            variant="body1"
            sx={{
              mt: 0.5,
              whiteSpace: 'pre-wrap',
              fontWeight: 700,
              color: (t) =>
                t.palette.mode === 'dark' ? t.palette.primary.light : t.palette.primary.dark,
            }}
          >
            {safeText(geminiText || 'No AI content returned yet.')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2 }}>
            <Button size="small" startIcon={<AutorenewIcon />} onClick={onRegenerate}>
              Regenerate
            </Button>
          </Box>
        </Paper>
      )}

      {/* Google Fact Check */}
      {!isAiView && googleFactCheck && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1">Google Fact Check</Typography>
          {Array.isArray(googleFactCheck.claims) && googleFactCheck.claims.length > 0 ? (
            googleFactCheck.claims.map((c, idx) => (
              <Box key={idx} sx={{ mt: idx === 0 ? 1 : 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {safeText(c.text)}
                </Typography>
                {Array.isArray(c.claimReview) &&
                  c.claimReview.length > 0 &&
                  c.claimReview.map((cr, i) => (
                    <Box key={i} sx={{ mt: 0.5 }}>
                      <Typography variant="body2">
                        {safeText(cr.publisher?.name)} — {safeText(cr.textualRating ?? cr.title)}
                      </Typography>
                      {cr.url && (
                        <Link href={safeText(cr.url)} target="_blank" rel="noreferrer" underline="hover">
                          {safeText(cr.title || cr.url)}
                        </Link>
                      )}
                    </Box>
                  ))}
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No Google fact-checks found.
            </Typography>
          )}
        </Paper>
      )}

      {/* News */}
      {!isAiView && Array.isArray(news) && news.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1">News Articles</Typography>
          {news.map((a, i) => {
            const title = safeText(a.title || a.headline || 'N/A');
            const longText = cleanText(a.excerpt ?? a.description ?? '');
            const textToShow = longText ? truncateWords(longText, 70) : '';
            const url = safeText(a.url || a.link || '');
            const src =
              safeText(a.source || a.source_name || a.site || sourceFromUrl(url) || 'N/A');
            const when = safeText(a.published_at ?? a.publishedAt ?? '');
            const whenFmt = when ? formatDate(when) : '';
            return (
              <Box key={i} sx={{ mt: i === 0 ? 1 : 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {title}
                </Typography>
                {textToShow ? (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {textToShow}
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ mt: 0.5 }} color="text.secondary">
                    No summary available.
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {src}
                  {whenFmt ? ` • ${whenFmt}` : ''}
                </Typography>
                {url && (
                  <Box sx={{ mt: 0.5 }}>
                    <Link href={url} target="_blank" rel="noreferrer">
                      Read source
                    </Link>
                  </Box>
                )}
              </Box>
            );
          })}
        </Paper>
      )}

      {/* Wikipedia */}
      {!isAiView && wiki && (wiki.extract || wiki.title) && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1">Wikipedia</Typography>
          {wiki.title && (
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              {safeText(wiki.title)}
            </Typography>
          )}
          {wiki.extract && (
            <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              {safeText(wiki.extract)}
            </Typography>
          )}
          {wiki.url && (
            <Box sx={{ mt: 1 }}>
              <Link href={wiki.url} target="_blank" rel="noreferrer">
                Read more
              </Link>
            </Box>
          )}
        </Paper>
      )}

      {/* Summary (claims view) */}
      {showSummary && !isAiView && (
        <Paper sx={{ p: 2, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" color="text.primary">
              Summary
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Tooltip title="Citations">
                <IconButton size="small" onClick={() => setCitOpen(true)}>
                  <LibraryBooksIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download full response">
                <IconButton size="small" onClick={handleDownloadFullResponse}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton
                size="small"
                aria-label="Play summary"
                onClick={() => {
                  try {
                    const txt = (
                      advancedAnalysis?.details ||
                      summary ||
                      claimText ||
                      ''
                    ).toString().trim();
                    if (typeof window !== 'undefined' && 'speechSynthesis' in window && txt) {
                      window.speechSynthesis.cancel();
                      window.speechSynthesis.speak(new SpeechSynthesisUtterance(txt));
                    }
                  } catch {}
                }}
              >
                <PsychologyIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => console.log('translate')}>
                <DescriptionIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Run advanced analysis"
                onClick={onAdvancedAnalysis}
                disabled={analysisLoading}
              >
                {analysisLoading ? <CircularProgress size={16} /> : <InsightsIcon fontSize="small" />}
              </IconButton>
              <IconButton size="small" onClick={() => console.log('bookmark')}>
                <BookmarkBorderIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => console.log('like')}>
                <FavoriteBorderIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={onShare}>
                <ShareIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Typography
            variant="body1"
            sx={{
              mt: 0.5,
              whiteSpace: 'pre-wrap',
              fontWeight: 700,
              color: (t) =>
                t.palette.mode === 'dark' ? t.palette.primary.light : t.palette.primary.dark,
            }}
          >
            {(advancedAnalysis && advancedAnalysis.details) || summary || claimText || '(no text)'}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2 }}>
            <Button size="small" startIcon={<AutorenewIcon />} onClick={onRegenerate} disabled={analysisLoading}>
              Regenerate
            </Button>
          </Box>
        </Paper>
      )}

      {/* Back to top */}
      {showScrollToTop && (
        <IconButton
          aria-label="Back to top"
          onClick={handleScrollToTop}
          sx={{
            position: 'fixed',
            right: 24,
            bottom: 220,
            zIndex: 9999,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': { bgcolor: 'primary.dark' },
            boxShadow: 3,
          }}
        >
          <KeyboardArrowUpIcon />
        </IconButton>
      )}

      {/* Citations dialog (shared) */}
      <Dialog open={citOpen} onClose={() => setCitOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Article Citations (AP format)</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            variant="outlined"
            multiline
            minRows={6}
            value={getCitationText()}
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              const blob = new Blob([getCitationText()], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'citations.txt';
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
            }}
          >
            Download
          </Button>
          <Button onClick={handleCopyCitations}>Copy</Button>
          <Button onClick={() => setCitOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
